import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { difference } from 'lodash';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';
import { QueryService } from '@vk-hr-tek/core/query';

import { EventListItem as Event } from '@app/gen/events';
import { ThunkExtra } from '@app/store';

import {
  completeLoading,
  reviewAdapter,
  ReviewState,
  ReviewWithRootState,
  setError,
  startLoading,
} from '../review.state';
import { GetReviewDto } from '../../dto';
import { EventsListService } from '../../services';

export const setFilters = createAction<{
  id: string;
  data: Filter[] | null;
}>('review/setFilters');

export const getEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    finished: number;
    tags?: string[];
    companyId?: string;
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/getEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().review;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters({ page: 'goal_management' }),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.get(getEventsDto, filters.data as Filter[]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    finished: number;
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/updateEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const filters = getState().review.filters.data;
      const eventListService = inject(EventsListService);

      const filter = inject(QueryService).parse(history.location.search);

      return await eventListService.get(
        {
          ...getEventsDto,
          ...(filter.filters ? { filters: filter.filters } : {}),
        },
        filters || [],
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось обновить заявки',
            message: 'Пожалуйста, попробуйте перезагрузить страницу',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalEvents = createAsyncThunk<
  {
    data: Event[];
    total: number;
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/getAdditionalEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().review;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters({ page: 'goal_management' }),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      return await eventsService.get(getEventsDto, filters.data || []);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const reviewListReducers = (
  builder: ActionReducerMapBuilder<EntityState<Event, string> & ReviewState>,
) => {
  builder.addCase(setFilters, (state, action) => {
    state.filters = action.payload;
  });
  builder.addCase(getEvents.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(getEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.total = payload.total;
    state.totalMy = payload.totalMy;
    state.totalMyTeam = payload.totalMyTeam;
    state.currentIds = payload.data.map(({ id }) => id);
    reviewAdapter.upsertMany(state, payload.data);
  });

  builder.addCase(getAdditionalEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.total = payload.total;
    state.currentIds = [
      ...state.currentIds,
      ...payload.data.map(({ id }) => id),
    ];
    reviewAdapter.upsertMany(state, payload.data);
  });
  builder.addCase(getEvents.rejected, (state, { payload, error, meta }) => {
    if (!meta.aborted) {
      setError(state, { payload, error });
    }
  });
  builder.addCase(updateEvents.fulfilled, (state, { payload, meta }) => {
    completeLoading(state);

    state.total = payload.total;

    const removedIds = difference(
      meta.arg.sortByIds || [],
      payload.data.map(({ id }) => id),
    );

    reviewAdapter.updateMany(
      state,
      payload.data.map((event) => ({
        id: event.id,
        changes: event,
      })),
    );

    reviewAdapter.removeMany(state, removedIds);
  });
};
